import "client-only"

import { type ICentraConfig } from "@/lib/centra-client/interfaces/ICentraConfig"
import ErrorUtil from "@/lib/error/ErrorUtil"
import { withAuthorization } from "@/lib/http/withAuthorization"
import type Logger from "@/lib/logger/Logger"
import { type Result, ResultFactory } from "@/types/generic/Result"

/**
 * The `FetchService` class handles HTTP requests and responses, centralizing network interactions for the Centra API.
 * It efficiently separates HTTP traffic management concerns from business logic, enhancing the clarity of the system.
 *
 * Can only be used on the client.
 *
 * Key Features:
 * - Configured with `ICentraConfig` and `Logger` for seamless integration with Centra's API and logging mechanisms.
 * - Implements a `fetch` method for HTTP requests, complete with error handling and response parsing.
 * - Automatically manages headers through the `CheckoutApiHeader` class, ensuring correct settings for API interactions.
 *
 * Example usage inside a service:
 * ```
 * public getCountryCodeByIp =
 *     (config: ICentraCheckoutApiRuntimeConfig) =>
 *     async (
 *         params: IGetCountryCodeByIdParams
 *     ): Promise<Result<CheckoutAPIGetCountriesAuto>> =>
 *         match(
 *             this.checkoutApiHeader.create(config, {
 *                 "X-Forwarded-For": params.ip,
 *             })
 *         )
 *             .with(
 *                 Ok,
 *                 async res =>
 *                     await this.fetchService.fetch<CheckoutAPIGetCountriesAuto>(
 *                         `/countries/auto`,
 *                         res.data
 *                     )
 *             )
 *             .with(Err, err =>
 *                 // Handle error
 *             )
 *             .exhaustive()
 * ```
 *
 * Essential for service classes like `CheckoutApiProductService`, `CheckoutApiCategoryService`, etc.,
 * the FetchService streamlines HTTP functionalities across the system.
 */
export class ClientFetchService {
    constructor(
        private config: ICentraConfig,
        private logger: Logger
    ) {}

    private static joinUrl(base: string, path: string): string {
        // Ensure there's exactly one slash between the base and the path
        return base.replace(/\/+$/, "") + "/" + path.replace(/^\/+/, "")
    }

    private static async extractErrorDetail(response: Response): Promise<string> {
        const contentType = response.headers.get("content-type")

        if (contentType?.includes("application/json")) {
            return JSON.stringify(await response.json())
        } else if (contentType?.includes("text")) {
            return await response.text()
        } else {
            return "Error response received with unsupported content type"
        }
    }

    public async fetch<T = unknown>(
        path: string,
        options: RequestInit = {}
    ): Promise<Result<T, Error>> {
        const headers = withAuthorization(options.headers, this.config.authorization)

        const defaultOptions: RequestInit = { method: "GET", headers }

        const mergedOptions: RequestInit = { ...defaultOptions, ...options, headers }

        let url = ""

        try {
            url = ClientFetchService.joinUrl(this.config.baseUrl, path)

            this.logger.debug(
                `FetchService: Starting ${mergedOptions.method ?? ""} request to '${url}'`
            )
            this.logger.debug("Fetch options: ", mergedOptions)

            const response = await fetch(url, mergedOptions)

            if (!response.ok) {
                const errorDetail = await ClientFetchService.extractErrorDetail(response)

                this.logger.error(
                    `HTTP Error ${response.status} for ${
                        options.method || "GET"
                    } ${url}: ${errorDetail}`
                )

                return ResultFactory.failure(
                    new Error(
                        `HTTP Error ${response.status} for ${
                            options.method || "GET"
                        } ${url}: ${errorDetail}`
                    )
                )
            }

            this.logger.debug(`FetchService: Request to ${url} successful.`)

            return ResultFactory.success((await response.json()) as T)
        } catch (e) {
            const handledError = ErrorUtil.handleUnknownError(e)

            const method = options.method || "GET"

            this.logger.error(
                `FetchService Error for ${method} ${url}: ${handledError.message}`
            )

            return ResultFactory.failure(
                new Error(
                    `FetchService Error for ${method} ${url}: ${handledError.message}`
                )
            )
        }
    }
}
